import { createStore } from 'react-sweet-state';
import { produce } from 'immer';
import { get, unionBy } from 'lodash';

import { BOOKMARK_TYPE, formatProjectTypeBookmarkName } from 'src/data/bookmark';
import {
  getOnboardingStageValue,
  isOnboardingStageFinished,
  ONBOARDING_STAGE_STATUS,
} from 'src/data/onboarding';
import { isDocumentProjectType, PROJECT_TYPE } from 'src/data/project';
import {
  acceptExternalSignup,
  authenticate,
  confirmRegistration,
  confirmTokenLogin,
  createExternalSignup,
  createUserBookmark,
  deleteUserBookmark,
  logout,
  queryCurrentUser,
  register,
  sendTrackingParams,
  updateCopilotDefaults,
  updateExtensionSettings,
  updateOnboardingStage,
  updateUser,
  updateUserCompanyInformation,
} from 'src/graphql/user';
import { getAnonymousSessionId } from 'src/lib/app';
import { Authentication } from 'src/lib/Authentication';
import { parseJson } from 'src/lib/common';
import { encodeSha1 } from 'src/lib/crypto';
import { isAbortError } from 'src/lib/errors';
import * as events from 'src/lib/events';
import * as gtm from 'src/lib/gtm';
import * as ls from 'src/lib/localData';
import { formatProjectCreateRoute } from 'src/lib/routing';
import { sentryClearUser, sentrySetUser } from 'src/lib/sentry';
import { getTrackingPayload } from 'src/lib/trackingPayload';
import { getDeviceType, isMobileDevice } from 'src/lib/userAgent';
import { ROUTE_PARAMS, ROUTES } from 'src/routes';

import { prepareUser } from './utils';

export const NAME = 'user';

const INITIAL_STATE = {
  currentUser: null,
  currentUserLoading: false,

  abortController: null,

  savingExtensionSettings: false,
  savingCopilotDefaults: false,
};

/**
 * Private actions
 */
export const PRIVATE_ACTIONS = {
  init:
    () =>
    async ({ dispatch }) => {
      if (window.location.pathname === ROUTES.MAGIC_LINK_CALLBACK) {
        Authentication.signOut();
        return;
      }

      try {
        const user = await dispatch(PRIVATE_ACTIONS.refreshCurrentUser());
        trackUserInitialRequestCompleted(user);
      } catch {
        /* do nothing */
      }
    },

  mergeIntoCurrentUser:
    (userData) =>
    ({ getState, setState }) => {
      const { currentUser } = getState();
      const newValue = {
        ...currentUser,
        ...userData,
      };
      setState({ currentUser: newValue });
    },

  mergeIntoOnboardingStages:
    (stageData) =>
    ({ getState, dispatch }) => {
      const { currentUser } = getState();
      if (!currentUser) {
        return;
      }
      const newStages = unionBy([stageData], currentUser.onboardingStages, 'name');
      dispatch(PRIVATE_ACTIONS.mergeIntoCurrentUser({ onboardingStages: newStages }));
    },

  mergeUserSettings:
    (newSettings) =>
    ({ getState, setState }) => {
      const { currentUser } = getState();
      const newUser = produce(currentUser, (draft) => {
        draft.settings = {
          ...draft.settings,
          ...newSettings,
        };
      });
      setState({ currentUser: newUser });
    },

  refreshCurrentUser:
    () =>
    async ({ getState, setState, dispatch }) => {
      if (!Authentication.isAuthenticated()) {
        throw new Error('Not authenticated');
      }

      // Cancel previous active request
      const { abortController: existingAbortController } = getState();
      if (existingAbortController) {
        existingAbortController.abort();
      }

      const abortController = new AbortController();
      setState({ currentUserLoading: true, abortController });

      try {
        const result = await queryCurrentUser({ abortSignal: abortController.signal });

        const user = prepareUser(result);

        setState({
          currentUser: user,
          currentUserLoading: false,
          abortController: null,
        });

        sentrySetUser(user);

        return user;
      } catch (e) {
        // We don't use "finally" for setting "loading" to "false", cause when current request gets aborted,
        // then we want "loading" to remain "true" for the new request
        if (!isAbortError(e)) {
          setState({ currentUserLoading: false });
          throw e;
        }
      }
    },

  trackUser: (source) => () => {
    sendTrackingParams({ source }).catch(() => {
      /* do nothing */
    });
  },

  clearState:
    () =>
    ({ setState }) => {
      setState({
        currentUser: null,
      });

      sentryClearUser();
    },
};

/**
 * Public available actions
 */
export const ACTIONS = {
  login:
    (email, password) =>
    ({ dispatch }) => {
      const device = getDeviceType();

      return authenticate({ email, password, device })
        .then(({ token }) => {
          return dispatch(ACTIONS.loginWithToken(token));
        })
        .catch((error) => {
          Authentication.signOut();
          throw error;
        });
    },

  loginOAuth:
    (data) =>
    ({ dispatch }) => {
      const { token, wasUserCreated } = data;

      return dispatch(ACTIONS.loginWithToken(token)).then((currentUser) => {
        if (wasUserCreated) {
          const customer = currentUser.customers?.[0]; // new user only has one customer
          gtm.pushDataLayerEvent(gtm.EVENTS.googleAuthFirstTime, {
            mobile: isMobileDevice(),
            email: currentUser.email,
            customer_id: customer ? customer.id : '',
            customer_email_sha1: encodeSha1(currentUser.email),
          });
          dispatch(PRIVATE_ACTIONS.trackUser('oauth'));
        }

        return currentUser;
      });
    },

  loginWithToken:
    (token) =>
    ({ dispatch }) => {
      Authentication.signIn(token);

      return dispatch(PRIVATE_ACTIONS.refreshCurrentUser());
    },

  register:
    (email, password, inviteToken) =>
    ({ dispatch }) => {
      const signUpDevice = getDeviceType();

      return register({ email, password, signUpDevice, referralToken: inviteToken })
        .then(({ token }) => {
          return dispatch(ACTIONS.loginWithToken(token));
        })
        .then((currentUser) => {
          dispatch(PRIVATE_ACTIONS.trackUser('email_signup'));

          return currentUser;
        });
    },

  confirmEmail:
    (confirmationToken) =>
    ({ dispatch }) => {
      const confirmationDevice = getDeviceType();

      return confirmRegistration({ confirmationToken, confirmationDevice })
        .then(({ token }) => {
          return dispatch(ACTIONS.loginWithToken(token));
        })
        .then(async (currentUser) => {
          const customer = currentUser.customers?.[0]; // new user only has one customer
          gtm.pushDataLayerEvent(gtm.EVENTS.userConfirmation, {
            mobile: isMobileDevice(),
            customer_id: customer ? customer.id : '',
            customer_email_sha1: await encodeSha1(currentUser.email),
          });
          dispatch(PRIVATE_ACTIONS.trackUser('email_verify'));

          return currentUser;
        });
    },

  createExternalSignup: (email) => () => {
    // Store "tracking payload" in signup invite, until it can be copied to "User" entity
    const trackingPayload = JSON.stringify(getTrackingPayload() || {});
    return createExternalSignup({
      email,
      trackingPayload,
      source: 'external_email_signup_request',
    });
  },

  acceptExternalSignup:
    (signupToken, password) =>
    ({ dispatch }) => {
      const signupDevice = getDeviceType();

      return acceptExternalSignup({ token: signupToken, password, signupDevice })
        .then(({ token }) => {
          return dispatch(ACTIONS.loginWithToken(token));
        })
        .then(async (currentUser) => {
          const customer = currentUser.customers?.[0]; // new user only has one customer
          gtm.pushDataLayerEvent(gtm.EVENTS.userConfirmation, {
            mobile: isMobileDevice(),
            customer_id: customer ? customer.id : '',
            customer_email_sha1: await encodeSha1(currentUser.email),
          });
          dispatch(PRIVATE_ACTIONS.trackUser('external_email_signup'));

          return currentUser;
        });
    },

  logout:
    (options = null) =>
    ({ dispatch }) => {
      if (options?.backendLogout) {
        // Notify BE about the "logout", no need to await it or handle errors
        logout(Authentication.getToken()).catch(() => null);
      }

      // Clear store state of log-outing user
      dispatch(PRIVATE_ACTIONS.clearState());

      // Reset in-browser auth state
      Authentication.signOut();

      // Clear some "session" local values when user logs out
      ls.coTutorialWasShownInSession.remove();

      // Clear selected category on logout
      ls.selectedProjectCategory.remove();
    },

  verifyAuthentication:
    () =>
    ({ dispatch }) => {
      return dispatch(PRIVATE_ACTIONS.refreshCurrentUser())
        .then(() => {
          Authentication.notifyExtension();
        })
        .catch((error) => {
          Authentication.signOut();
          throw error;
        });
    },

  refreshCurrentUser:
    () =>
    ({ dispatch }) => {
      return dispatch(PRIVATE_ACTIONS.refreshCurrentUser());
    },

  finishOnboardingStage:
    (stageName) =>
    ({ getState, dispatch }) => {
      const { currentUser } = getState();

      // Maybe this stage was already finished
      if (isOnboardingStageFinished(currentUser.onboardingStages, stageName)) {
        return Promise.resolve();
      }

      return updateOnboardingStage({
        name: stageName,
        status: ONBOARDING_STAGE_STATUS.finished,
      }).then((stageData) => {
        dispatch(PRIVATE_ACTIONS.mergeIntoOnboardingStages(stageData));
      });
    },

  isFinishedOnboardingStage:
    (stageName) =>
    ({ getState }) => {
      const { currentUser } = getState();
      const onboardingStages = get(currentUser, 'onboardingStages');
      return isOnboardingStageFinished(onboardingStages, stageName);
    },

  updateOnboardingStageValue:
    (stageName, value, status) =>
    async ({ dispatch }) => {
      const stageData = await updateOnboardingStage({
        name: stageName,
        value,
        status,
      });
      dispatch(PRIVATE_ACTIONS.mergeIntoOnboardingStages(stageData));
    },

  getOnboardingStageValue:
    (stageName) =>
    ({ getState }) => {
      const { currentUser } = getState();
      const onboardingStages = get(currentUser, 'onboardingStages');
      return getOnboardingStageValue(onboardingStages, stageName);
    },

  createProjectTypeBookmark:
    (projectType, generationTool) =>
    ({ getState, dispatch }) => {
      const name = formatProjectTypeBookmarkName(projectType);
      const link = isDocumentProjectType(projectType)
        ? formatProjectCreateRoute(projectType, {
            [ROUTE_PARAMS.generationTool]: generationTool,
          })
        : formatProjectCreateRoute(projectType);
      const metadata = { projectType, generationTool };

      return createUserBookmark({
        type: BOOKMARK_TYPE.projectType,
        name,
        link,
        metadata,
      }).then((bookmark) => {
        const { currentUser } = getState();
        const newList = [...currentUser.bookmarks, bookmark];
        dispatch(PRIVATE_ACTIONS.mergeIntoCurrentUser({ bookmarks: newList }));
      });
    },

  getProjectTypeBookmark:
    ({ projectType, generationTool }) =>
    ({ getState }) => {
      const { currentUser } = getState();

      const bookmarks = (currentUser && currentUser.bookmarks) || [];
      return bookmarks.find((bookmark) => {
        // If it's a newer bookmark, and has metadata, then search by it
        const metadata = parseJson(bookmark.metadata);
        if (metadata) {
          return (
            metadata.projectType === projectType &&
            (!generationTool || metadata.generationTool === generationTool)
          );
        }

        // If there is no metadata, then try searching by link URL
        const link = formatProjectCreateRoute(projectType, {
          [ROUTE_PARAMS.generationTool]: generationTool,
        });
        return bookmark.link === link;
      });
    },

  isGenerationToolBookmarked:
    (generationTool) =>
    ({ dispatch }) => {
      const bookmark = dispatch(
        ACTIONS.getProjectTypeBookmark({
          projectType: PROJECT_TYPE.document,
          generationTool,
        })
      );
      return bookmark != null;
    },

  deleteProjectTypeBookmark:
    (projectType, generationTool) =>
    ({ getState, dispatch }) => {
      const bookmark = dispatch(ACTIONS.getProjectTypeBookmark({ projectType, generationTool }));
      if (!bookmark) {
        return Promise.resolve();
      }

      return deleteUserBookmark({ id: bookmark.id }).then(() => {
        const { currentUser } = getState();
        const newList = (currentUser.bookmarks || []).filter((item) => item.id !== bookmark.id);
        dispatch(PRIVATE_ACTIONS.mergeIntoCurrentUser({ bookmarks: newList }));
      });
    },

  updateCompanyInformation:
    ({ userRoles, companySize, companyWebsite }) =>
    ({ dispatch }) => {
      return updateUserCompanyInformation({
        userRoles,
        companySize,
        companyWebsite,
      }).then((updatedUser) => {
        // Parse "company information" from JSON
        const { segment, companyInformation } = prepareUser(updatedUser);

        // Merge updated info into "current user"
        dispatch(PRIVATE_ACTIONS.mergeIntoCurrentUser({ segment, companyInformation }));
      });
    },

  getCurrentUser:
    () =>
    ({ getState }) => {
      return getState().currentUser;
    },

  getCurrentUserId:
    () =>
    ({ getState }) => {
      const { currentUser } = getState();
      return currentUser ? currentUser.id : null;
    },

  isCurrentUserConfirmed:
    () =>
    ({ getState }) => {
      const { currentUser } = getState();
      return currentUser?.isConfirmed || false;
    },

  setName:
    (name) =>
    ({ getState, setState }) => {
      const { currentUser } = getState();

      setState({ currentUser: { ...currentUser, name } });
    },

  updateUser: (customerId, defaultWorkspaceId) => () => {
    return updateUser({ customerId, defaultWorkspaceId });
  },

  updateCopilotDefaults:
    (customerId, defaultWorkspaceId, options) =>
    async ({ setState, dispatch }) => {
      setState({ savingCopilotDefaults: true });
      try {
        const user = await updateCopilotDefaults({
          customerId,
          workspaceId: defaultWorkspaceId,
          ...options,
        });
        if (user) {
          dispatch(
            PRIVATE_ACTIONS.mergeIntoCurrentUser({
              copilotCustomerId: user.settings?.copilotCustomerId ?? null,
              copilotWorkspaceId: user.settings?.copilotWorkspaceId ?? null,
              copilotTov: user.settings?.copilotTov ?? null,
              copilotSfv: user.settings?.copilotSfv ?? null,
              copilotImproveKeepStructure: user.settings?.copilotImproveKeepStructure ?? false,
            })
          );
        }
        return user;
      } finally {
        setState({ savingCopilotDefaults: false });
      }
    },
  confirmTokenLogin:
    (authToken) =>
    ({ dispatch }) => {
      const loginDevice = getDeviceType();

      return confirmTokenLogin({ authToken, loginDevice }).then(({ token }) => {
        return dispatch(ACTIONS.loginWithToken(token));
      });
    },

  saveExtensionSettings:
    ({ customerId, workspaceId }, options) =>
    async ({ setState, dispatch }) => {
      setState({ savingExtensionSettings: true });
      try {
        const result = await updateExtensionSettings(
          { extensionCustomerId: customerId, extensionWorkspaceId: workspaceId },
          options
        );

        dispatch(
          PRIVATE_ACTIONS.mergeUserSettings({
            extensionCustomerId: result.extensionCustomerId,
            extensionWorkspaceId: result.extensionWorkspaceId,
          })
        );

        return result;
      } finally {
        setState({ savingExtensionSettings: false });
      }
    },

  saveExtensionSettingsIfEmpty:
    ({ customerId, workspaceId }) =>
    async ({ dispatch }) => {
      if (!customerId || !workspaceId) {
        return;
      }

      const currentUser = dispatch(ACTIONS.getCurrentUser());
      if (!currentUser) {
        return;
      }

      if (
        String(currentUser.settings?.extensionCustomerId) === String(customerId) &&
        String(currentUser.settings?.extensionWorkspaceId) === String(workspaceId)
      ) {
        return;
      }

      await dispatch(ACTIONS.saveExtensionSettings({ customerId, workspaceId }));
    },
};

const Store = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});

export default Store;

const trackUserInitialRequestCompleted = async (user) => {
  try {
    const eventName = events.APP.currentUserInitialRequestCompleted;
    const attributes = {
      sessionId: getAnonymousSessionId(),
    };
    if (user?.isConfirmed) {
      events.trackEvent(eventName, attributes);
    } else {
      events.trackUserEventNoAuth(eventName, attributes);
    }
  } catch {
    // do nothing
  }
};
